import {NumberAnimator} from '@megaphonenpm/mputils/src/NumberAnimator.js';
import {
  CHANGE
} from '@megaphonenpm/mputils/src/constants.js';
import {
  div,
  fitMinWidthToText, getCSSRule,
  getStyleSheet,
  hide,
  onAdded,
  setCSSRule,
  setText,
  show
} from '@megaphonenpm/mputils/src/DOM.js';
import {cloneInstance} from "../GWDService.js";
import {cascadingGet, getAverageVote, isNil, notNil, notEmpty} from '@megaphonenpm/mputils/src/utils.js';
import {warn} from '@megaphonenpm/mputils/src/logfunctions.js';
import {MegaScreen} from "../MegaScreen.js";
import {play} from "../Animation.js";
import {ScreenComponent} from "./ScreenComponent.js";

// TODO: Refactor to subclass of InstrumentComponent
export class AnswersFivestarsComponent extends ScreenComponent {
  constructor (themeID) {
    super(themeID, ['average_vote_txt', 'StarBackground', 'StarForeground']);

    // Animates Five Stars average vote
    this.numberAnimator = new NumberAnimator();
    this.numberAnimator.on(CHANGE, this.averageVoteAnimatorChangeListener.bind(this));
    this.numberAnimator.duration = 3000;
  }

  build () {
    super.build();

    // Pre-flight check
    if (!this.valid) {
      return;
    }

    // Create outer-most container
    this.view = div('answersFivestarsContainer', 'answersContainer');
    this.view.classList.add('overflowHidden');

    // Indicate the states during which the view should be visible
    this.view.classList.add('state_scene', 'state_scene_result');
    this.view.classList.add('product_fivestars');

    // Create container for stars and average vote
    this.starsAndAverageVoteContainer = div(null, 'starsAndAverageVoteContainer');
    this.starsAndAverageVoteContainer.classList.add('flexExpandingItem', 'flex', 'flexCenterChildren', 'constrainHeight');

    // Create container for vote result
    this.finalResultContainer = div(null, 'fivestarsFinalResultContainer');
    this.finalResultContainer.classList.add('flexExpandingItem', 'flex', 'flexCenterChildren');
    let finalResultBackgroundColor = this.getThemeSetting('design.products.fivestars.screen.result.background_color');
    if (notNil(finalResultBackgroundColor)) {
      this.finalResultContainer.style.background = finalResultBackgroundColor;
    }

    // Clone scene-result content from GWD theme
    this.fivestarsResult = cloneInstance(this.themeID, 'fivestarsResult');
    if (isNil(this.fivestarsResult)) {
      // No FiveStars-specific "scene result" was found, so attempt to clone the generic one.
      // In themes that do not have a custom result layout for Mood Meter, Guess Gauge, and Five Stars,
      // the "averageVoteResult" is used for all of those products.
      this.fivestarsResult = cloneInstance(this.themeID, 'averageVoteResult');
    }

    // Add scene result to container, and configure it
    if (this.fivestarsResult) {
      this.finalResultContainer.append(this.fivestarsResult);
      setText(this.fivestarsResult.querySelectorAll('.audience_average_txt, .fivestars_audience_average_txt'),
        this.getLocaleString('audience_average'));
    }

    // Create container hierarchy for star backplate, background stars, and foreground stars
    this.starsBackplate = div(null, "starsBackplate");
    this.starsOuterContainer = div(null, 'starsOuterContainer');
    this.starsBackgroundContainer = div(null, 'starsBackgroundContainer');
    this.starsBackgroundContainer.classList.add('starsContainer');
    this.starsForegroundContainer = div(null, 'starsForegroundContainer');
    this.starsForegroundContainer.classList.add('starsContainer');
    this.starsOuterContainer.append(this.starsBackgroundContainer, this.starsForegroundContainer);
    this.starsBackplate.append(this.starsOuterContainer);

    // Configure star backplate
    let starsBackplateBackgroundColor = this.getThemeSetting('design.products.fivestars.screen.stars.backplate.background_color');
    if (notEmpty(starsBackplateBackgroundColor)) {
      this.starsBackplate.style.background = starsBackplateBackgroundColor;
    }
    let expandStarsBackground = this.getThemeSetting('design.products.fivestars.screen.stars.backplate.expand');
    if (expandStarsBackground === true) {
      this.starsBackplate.classList.add('flexExpandingItem');
    }

    // Create average vote text
    this.averageVote = cloneInstance(this.themeID, 'average_vote_txt');
    this.starsAndAverageVoteContainer.append(this.starsBackplate, this.averageVote);

    // Configure average vote text
    let averageVoteGap = this.getThemeSetting('design.products.fivestars.screen.average_label.gap.horizontal');
    if (notNil(averageVoteGap)) {
      this.averageVote.style.marginLeft = `${averageVoteGap}px`;
    }

    // Create a single background star (to be cloned later)
    let starBackgroundFilterWrapper = div(null, 'StarBackgroundFilterWrapper');   // Outer-most <div>, where drop shadows or other filters are applied
    let starBackgroundWrapper = div(null, 'StarBackgroundWrapper');  // An inner <div> to contain the star image
    let starBackground = cloneInstance(this.themeID, 'StarBackground');  // The star background image from the GWD theme
    starBackgroundWrapper.append(starBackground);
    starBackgroundFilterWrapper.append(starBackgroundWrapper);

    // Create a single foreground star (to be cloned later)
    let starForegroundWrapper = div(null, 'StarForegroundWrapper');
    let starForeground = cloneInstance(this.themeID, 'StarForeground');  // The star foreground image from the GWD theme
    starForegroundWrapper.append(starForeground);

    // Determine gap between stars
    let starGap = this.getThemeSetting('design.products.fivestars.screen.stars.gap.horizontal');

    // Clone four more background and four more foreground stars. Apply gap between stars per theme settings.
    for (let i = 0; i < 5; i++) {
      if (notNil(starBackground)) {
        let thisStarBackground = starBackgroundFilterWrapper.cloneNode(true);
        if (notNil(starGap) && i > 0) {
          thisStarBackground.style.marginLeft = `${starGap}px`;
        }
        this.starsBackgroundContainer.append(thisStarBackground);
      }
      if (notNil(starForeground)) {
        let thisStarForeground = starForegroundWrapper.cloneNode(true);
        if (notNil(starGap) && i > 0) {
          thisStarForeground.style.marginLeft = `${starGap}px`;
        }
        this.starsForegroundContainer.append(thisStarForeground);
      }
    }

    // Determine star colors, per theme settings (in preparation for final star configuration).
    //
    // SUMMARY
    //    If the theme object has a color assigned for the star foreground:
    //      IF the star foreground is an <img> element whose src is an .svg file, then use that .svg file as a mask
    //      ELSE ignore the color setting (because only .svg files can be used reliable for element masks).
    //      ...repeat the above for star background.
    //   It would be far preferable to simply assign the color directly to the loaded star asset, but as of 2020,
    //   no such function or filter exists to do so in JavaScript or CSS. Hence the following code...
    let starForegroundColor = this.getThemeSetting('design.products.fivestars.screen.stars.foreground_color');
    let starBackgroundColor = this.getThemeSetting('design.products.fivestars.screen.stars.background_color');
    let thisThemeStylesheet = getStyleSheet(MegaScreen.globals.themeService.getThemeFieldName(this.themeID));

    // NESTED FUNCTION: configureStarGroup()
    //   Checks if a theme setting was supplied, and if so, attempts to apply it to the specified group of stars
    //   (i.e., the background stars or the foreground stars)
    let configureStarGroup = (starElement, themeSettingStarColor, starSelector, starWrapperSelector) => {
      if (notEmpty(themeSettingStarColor)) {
        // If the supplied starElement is an image...
        if (starElement.nodeName.toLowerCase() === 'img') {
          // ...and if the starElement is a .svg file...
          if (notNil(starElement.src) && starElement.src.endsWith('svg')) {
            // ...then the starElement as a mask for its containing star wrapper. The star wrapper will be colored to match the theme setting.
            applyStarThemeSettings(starSelector, starWrapperSelector, themeSettingStarColor, starElement.src);
          } else {
            // Can't apply color to non-svg stars.
            // The theme object specifies a color setting for the starElement, but the starElement from
            // GWD is not an .svg file, so the color can't be applied. Warn the desginer.
            warn(`Could not apply theme color setting ${themeSettingStarColor} to target ${starSelector}.`
              + ` To apply theme color settings to stars, ensure that the GWD file uses .svg format for ${starSelector}`);
          }
        }
      }
    };

    // CSS manipulations are coming up, so prepare by determining the theme's CSS prefix
    let themeCSSPrefix = MegaScreen.globals.themeService.getCSSPrefixForTheme(this.themeID);

    // NESTED FUNCTION: applyStarThemeSettings()
    //   Applies an external theme-color setting to foreground stars or background stars
    let applyStarThemeSettings = (starSelector, starWrapperSelector, starColor, starURL) => {
      // To apply a star's color setting, we treat the star <img> elemnts as masks only.

      // First task: hide the <img> mask with a CSS property:
      let starRule = getCSSRule(thisThemeStylesheet, `${themeCSSPrefix} ${starSelector}`);
      starRule.style.display = 'none';

      // Now apply the star mask to the star's wrapper <div>, and set the wrapper <div>'s color
      setCSSRule(thisThemeStylesheet, `${themeCSSPrefix} ${starWrapperSelector}`,
        `background:${starColor};
          -webkit-mask-image: url(${starURL});
          -webkit-mask-repeat: no-repeat;
          -webkit-mask-size: 100%;
      
          mask-image: url(${starURL});
          mask-repeat: no-repeat;
          mask-size: 100%;
        `);

      // Match the star wrapper div's size to the star it contains (as specified in the GWD theme).
      // This is required to ensure that starWrapper <divs> occupy space when their content star
      // <img> is hidden due to masking.
      let starWrapperRule = getCSSRule(thisThemeStylesheet, `${themeCSSPrefix} ${starWrapperSelector}`);
      starWrapperRule.style.width = starRule.style.width;
      starWrapperRule.style.height = starRule.style.height;
    };

    // Nested function are ready for use, so apply star color theme settings now
    configureStarGroup(starForeground, starForegroundColor, '.StarForeground','.StarForegroundWrapper');
    configureStarGroup(starBackground, starBackgroundColor, '.StarBackground','.StarBackgroundWrapper');

    // Apply theme setting for the star drop shadow (if any)
    let starBackgroundShadow = this.getThemeSetting('design.products.fivestars.screen.stars.shadow');
    if (notEmpty(starBackgroundShadow)) {
      setCSSRule(thisThemeStylesheet,
        `${themeCSSPrefix} .StarBackgroundFilterWrapper`,
        `filter: drop-shadow(${starBackgroundShadow})`);
    }

    // Append all content to the view
    this.view.append(this.starsAndAverageVoteContainer);
    this.view.append(this.finalResultContainer);
  }

  /**
   * Updates the on-screen display of stars and numeric rating (e.g., 3.5).
   */
  updateAverageVote () {
    if (!this.valid) {
      return;
    }
    // Ensure the average vote element is wide enough to fit the widest possible average-vote text,
    // which is '3.5'. Theoretically, the font could change at any time for average_vote_txt,
    // so recalculate the minimum width every time the average vote changes.
    // The extra "." in "3..5" provides room for fonts with unusually wide numbers.
    fitMinWidthToText(this.view, '.average_vote_txt', '3..5')

    // Start playing the numeric animation for the "average vote" text output
    let votes = MegaScreen.globals.appState.votes;
    let averageAsPercentage = getAverageVote(votes);
    this.numberAnimator.animateTo([averageAsPercentage]);

    // Calculate and assign the new visible width of the star foreground, including accounting
    // for the visual gaps between stars.
    let currentPerent = averageAsPercentage/100;
    let numAnswers = votes.length;
    let currentAverageVote = currentPerent * numAnswers;
    let starGap = this.getThemeSetting('design.products.fivestars.screen.stars.gap.horizontal', 0); // Default to 0
    let numFullStars = Math.floor(currentAverageVote);

    // The amount of horizontal space occupied by gaps between stars
    let totalGapWidth = ((numAnswers-1) * starGap);
    // TODO: Consider deriving totalStarWidth from the actual individual foreground
    //  star images rather than starsBackgroundContainer. If starsBackgroundContainer has
    //  a dropshadow on the stars, it might be wider than the foreground, which would throw
    //  off subsequent calculations.
    // The amount of horizontal space occupied by stars
    let totalStarWidth = this.starsBackgroundContainer.offsetWidth - totalGapWidth;
    let starGapPadding = 0;
    // Calculate the padding required for the gaps between stars
    if ((currentAverageVote > 1) && (currentAverageVote < numAnswers)) {
      starGapPadding = starGap * numFullStars;
    } else if (currentAverageVote === numAnswers) {
      starGapPadding = starGap * numAnswers-1;
    }
    // The width is the percentage of the actual stars that matches the current vote,
    // PLUS the cumulative padding between stars that are either completely or partially filled.
    let newForegroundWidth = (totalStarWidth * currentPerent) + starGapPadding;
    this.enableStarAnimation();
    this.starsForegroundContainer.style.width = `${newForegroundWidth}px`;
  }

  disableStarAnimation () {
    if (!this.valid) {
      return;
    }
    this.starsForegroundContainer.classList.remove('transitionWidthAverageVote');
  }

  enableStarAnimation () {
    if (!this.valid) {
      return;
    }
    this.starsForegroundContainer.classList.add('transitionWidthAverageVote');
  }

  resetAverageVote () {
    if (!this.valid) {
      return;
    }
    this.disableStarAnimation();
    this.starsForegroundContainer.style.width = '0';
    this.numberAnimator.skipTo([0]);
  }

  averageVoteAnimatorChangeListener () {
    this.displayAverageVote(
      MegaScreen.globals.sceneService.getStarRating(
        this.numberAnimator.current[0],
        MegaScreen.globals.appState.votes.length
      ),
      [this.averageVote]
    );
  }

  /**
   * Displays the average vote as text such as "3.5" in all elements specified by elementList.
   */
  displayAverageVote (value, elementList) {
    setText(elementList, value);
  }


  /**
   * Plays the intro animations.
   */
  playIntro () {
    let fivestarsStartAnimation = cascadingGet(this.theme,
      [`design.products.fivestars.screen.animation.main.start.label`,
        `design.default.screen.animation.main.start.label`]);
    let delay = cascadingGet(this.theme,
      [`design.products.fivestars.screen.animation.main.start.delay`,
        `design.default.screen.animation.main.start.delay`]);

    if (notNil(fivestarsStartAnimation)) {
      play(this.view, fivestarsStartAnimation, delay);
    }
  }

  /**
   * Displays the final vote result after the "polls are closed."
   */
  startSceneResult () {
    if (!this.valid) {
      return;
    }
    hide(this.starsAndAverageVoteContainer);
    show(this.finalResultContainer);
    this.displayAverageVote(MegaScreen.globals.sceneService.getStarRatingFromVotes(MegaScreen.globals.appState.votes),
      this.finalResultContainer.querySelectorAll('.fivestars_result_txt'));

    let fivestarsResultAnimation = cascadingGet(this.theme,
      [`design.products.fivestars.screen.animation.result.start.label`,
        `design.default.screen.animation.result.start.label`]);

    if (notNil(fivestarsResultAnimation)) {
      play(this.finalResultContainer, fivestarsResultAnimation);
    }
  }

  revertSceneResult () {
    show(this.starsAndAverageVoteContainer);
    hide(this.finalResultContainer);
  }

  reset () {
    this.revertSceneResult();
    this.resetAverageVote();
  }
}




















